/**
 * LandingPage — a vitrine do Orçamigo, em `/`.
 *
 * ─── QUEM VÊ ESTA TELA ──────────────────────────────────────────────────────
 *
 * Só quem chega pela WEB e ainda não tem sessão. As outras duas situações
 * continuam como antes, e as duas por um motivo próprio:
 *
 * · No app INSTALADO (`Capacitor.isNativePlatform()`) a rota `/` segue sendo o
 *   `OnboardingPage`. Uma vitrine dentro do aplicativo seria uma propaganda
 *   para quem já comprou — e o carrossel de lá termina em "Entrar ou criar
 *   conta" e "Testar sem criar conta", que é o que essa pessoa precisa.
 *   É por PLATAFORMA, e não por domínio: `app.orcamigo.com.br` responde 302
 *   para o site (a hospedagem serve um único domínio), então o hostname não
 *   distingue nada.
 * · Logado, `/` manda para `/dashboard`, exatamente como o onboarding já fazia.
 *
 * ─── O QUE ESTA PÁGINA PODE AFIRMAR ─────────────────────────────────────────
 *
 * Só o que o produto faz de fato. Não há número de usuários, depoimento,
 * avaliação nem preço nesta tela: preço vive no RevenueCat e mudá-lo lá
 * deixaria a vitrine mentindo sem ninguém perceber. O plano PRO é apresentado
 * pelo que ele libera e pelos 7 dias de teste; o valor aparece na assinatura,
 * onde é sempre o verdadeiro — e a seção de planos DIZ isso, porque preço
 * ausente sem explicação lê como esconderijo.
 *
 * Pelo mesmo motivo, o mockup de celular do hero é feito de BARRAS abstratas:
 * nenhum "R$ <número>" pode aparecer no texto renderizado (o teste desta
 * página reprova), então o total do documento desenhado é uma barra verde,
 * nunca um valor.
 *
 * O caminho de VISITANTE ("Testar sem criar conta") é preservado de propósito:
 * ele existe hoje no onboarding e é o que deixa a pessoa montar um orçamento
 * inteiro antes de decidir (`/quotes/new` funciona sem sessão) — e, ao criar a
 * conta, o `GuestQuoteImportPrompt` oferece importar o que ela montou.
 */
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  FileText,
  FileSignature,
  ClipboardList,
  ReceiptText,
  Receipt,
  Wallet,
  CalendarDays,
  Package,
  Users,
  BookOpen,
  ShoppingCart,
  WifiOff,
  Check,
  ArrowRight,
  ChevronDown,
  Send,
} from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const LOGO =
  'https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/brand-assets/logo-orcamigo.png';

/** O trabalho principal, na língua de quem orça no bloco de notas do WhatsApp. */
const PASSOS = [
  {
    titulo: 'Monte o orçamento',
    texto: 'Escolha os itens do seu catálogo, ajuste o preço e pronto — dá para fazer no meio do serviço.',
  },
  {
    titulo: 'Mande pelo WhatsApp',
    texto: 'O PDF sai arrumado, com os seus dados e os do cliente. Um toque e já foi.',
  },
  {
    titulo: 'O cliente aprova pelo link',
    texto:
      'Sem baixar nada: ele abre no celular dele e aprova. O status muda sozinho no app. O contrato funciona igual — assinado pelo celular, por um link.',
  },
] as const;

/** O ciclo comercial que o app fecha, na ordem em que ele acontece. */
const CICLO = [
  { icon: FileText, titulo: 'Orçamento', texto: 'Monte a proposta com itens do seu catálogo e envie por WhatsApp.' },
  { icon: FileSignature, titulo: 'Contrato', texto: 'O cliente assina pelo celular, por um link — sem imprimir nada.' },
  { icon: ClipboardList, titulo: 'Ordem de serviço', texto: 'Registre equipamento, diagnóstico e a assinatura na entrega.' },
  { icon: ReceiptText, titulo: 'Recibo', texto: 'Comprovante de pagamento com valor por extenso, pronto para enviar.' },
  { icon: Receipt, titulo: 'Nota fiscal', texto: 'NFS-e para quem tem CNPJ MEI, direto do aplicativo.' },
] as const;

/** O que sustenta o dia a dia em volta dos documentos. */
const MODULOS = [
  { icon: Wallet, titulo: 'Financeiro', texto: 'Quem já pagou, quem falta pagar e o que venceu.' },
  { icon: CalendarDays, titulo: 'Agenda', texto: 'Compromissos e vencimentos no mesmo calendário.' },
  { icon: Users, titulo: 'Clientes', texto: 'Cada cliente com o histórico de tudo o que já foi feito.' },
  { icon: BookOpen, titulo: 'Catálogo', texto: 'Serviços e produtos com preço, custo e unidade.' },
  // "Automática, se você quiser" é a verdade: o padrão de fábrica das
  // configurações de baixa é PERGUNTAR — automático é opt-in.
  { icon: Package, titulo: 'Estoque', texto: 'Baixa quando o orçamento é aprovado — automática, se você quiser.' },
  { icon: ShoppingCart, titulo: 'Venda rápida', texto: 'Vendeu no balcão? Registra em segundos, sem montar orçamento.' },
] as const;

/**
 * As duas listas são conferidas contra `src/lib/features.ts`, que é quem
 * decide de verdade — o que está `free: 'allowed'` pode ser prometido aqui.
 * ⚠️ `inventory` (Estoque) é `free: 'upsell'`, então ele é PRO: a versão
 * anterior desta página o prometia no grátis, o que era mentira na tela mais
 * visível do produto.
 */
const NO_PLANO_GRATIS = [
  'Orçamentos, ordens de serviço e recibos',
  'Clientes e catálogo de serviços e produtos',
  'Financeiro, agenda e venda rápida',
  'PDF pronto para enviar por WhatsApp',
] as const;

const NO_PLANO_PRO = [
  'Orçamentos sem limite mensal',
  'Contratos com assinatura digital',
  'Controle de estoque com baixa nas vendas',
  'Modelos de PDF e cor da sua marca',
  'Relatórios avançados e exportação',
  'Fotos nos itens e impressão em bobina',
] as const;

/**
 * FAQ 100% factual — cada resposta é conferível no produto. Nenhuma pode
 * conter "R$ <número>" (o guard de preço varre o textContent inteiro, e o
 * conteúdo de um <details> fechado também entra nele).
 */
const PERGUNTAS = [
  {
    p: 'Preciso pagar para começar?',
    r: 'Não. O plano grátis não pede cartão e já faz orçamento, ordem de serviço e recibo. O PRO tem 7 dias de teste.',
  },
  {
    p: 'Funciona no meu celular?',
    r: 'Funciona direto no navegador, sem instalar nada. É só abrir e usar.',
  },
  {
    p: 'Meu cliente precisa baixar alguma coisa?',
    r: 'Não. Ele recebe um link, abre no celular dele e aprova o orçamento — ou assina o contrato — ali mesmo.',
  },
  {
    p: 'E se eu estiver num lugar sem sinal?',
    r: 'O app continua funcionando: você monta o orçamento offline e tudo sobe sozinho quando a internet volta.',
  },
  {
    p: 'Preciso ter CNPJ?',
    r: 'Não. Orçamento, recibo e ordem de serviço funcionam para qualquer um. A nota fiscal (NFS-e) é para quem tem CNPJ MEI.',
  },
  {
    p: 'O que acontece depois dos 7 dias do PRO?',
    r: 'Você escolhe: assina o PRO ou continua no plano grátis. Seus orçamentos e clientes continuam com você.',
  },
  {
    p: 'Dá para testar antes de criar conta?',
    r: 'Sim. O botão "Testar sem criar conta" abre um orçamento de verdade, direto no navegador. Quando você cria a conta, o app oferece importar o que você montou.',
  },
] as const;

/** As âncoras do cabeçalho — cada uma aponta para o `id` de uma seção. */
const ANCORAS = [
  { id: 'como-funciona', rotulo: 'Como funciona' },
  { id: 'recursos', rotulo: 'Recursos' },
  { id: 'planos', rotulo: 'Planos' },
  { id: 'perguntas', rotulo: 'Dúvidas' },
] as const;

const CARTAO = 'rounded-2xl border border-border/60 bg-card p-5 shadow-sm';
// Cards "vivos" (ciclo, módulos, passos) ganham o hover de elevação; os de
// plano ficam estáticos — ali o movimento distrairia da comparação.
const CARTAO_VIVO = cn(CARTAO, 'transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md');

/** Uma linha abstrata de item do documento do mockup (barra + valor). */
function LinhaDeItem({ larguraValor }: { larguraValor: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="h-2 min-w-0 flex-1 rounded bg-muted-foreground/25" />
      <div className={cn('h-2 shrink-0 rounded bg-muted-foreground/40', larguraValor)} />
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Mesmo comportamento do onboarding: quem já tem sessão não vê a vitrine.
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  // SEO da vitrine: título e descrição próprios enquanto ELA está na tela.
  // Restaurados no cleanup porque o app não gerencia <title> por rota — sem
  // isso, o título de marketing vazaria para /auth e todo o resto da sessão.
  useEffect(() => {
    const tituloAnterior = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const descricaoAnterior = meta?.getAttribute('content') ?? null;
    document.title = 'Orçamigo — orçamento, contrato, ordem de serviço, recibo e NFS-e';
    meta?.setAttribute(
      'content',
      'Monte o orçamento em minutos, envie por WhatsApp e o cliente aprova pelo link. Contrato assinado pelo celular, recibo e NFS-e para MEI. Funciona offline, direto no navegador. Grátis, sem cartão.',
    );
    return () => {
      document.title = tituloAnterior;
      if (descricaoAnterior !== null) meta?.setAttribute('content', descricaoAnterior);
    };
  }, []);

  const irPara = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen bg-background">
      {/* Keyframes locais: classe animate-[nome_…] sem o @keyframes correspondente
          é no-op SILENCIOSO (família #211) — por isso eles moram aqui, junto de
          quem os usa. Loops só rodam sob `motion-safe:`. */}
      <style>{`
        @keyframes aparecer { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: none } }
        @keyframes flutuar { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
        @keyframes sincronizar { 0% { width: 15% } 70%, 100% { width: 100% } }
      `}</style>

      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <img src={LOGO} alt="Orçamigo" className="h-auto w-32 sm:w-40" width={1536} height={585} />
          {/* Âncoras só a partir de md: em 360px, logo + Entrar + CTA já ocupam a linha (#67/#186). */}
          <nav aria-label="Seções da página" className="hidden items-center gap-1 md:flex">
            {ANCORAS.map(({ id, rotulo }) => (
              <button
                key={id}
                onClick={() => irPara(id)}
                className="rounded-full px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
              >
                {rotulo}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/auth')}
              className="rounded-full px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
            >
              Entrar
            </button>
            <Button3D size="sm" onClick={() => navigate('/auth')}>
              <span className="sm:hidden">Criar conta</span>
              <span className="hidden sm:inline">Criar conta grátis</span>
            </Button3D>
          </div>
        </div>
      </header>

      <main>
        {/* ─── Chamada ───────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-emerald-700 px-4 py-14 text-white sm:py-20">
          {/* Camadas decorativas — o overflow-hidden da seção recorta os blobs (#185). */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
          />
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-300/10 blur-3xl" />

          <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div className="text-center motion-safe:animate-[aparecer_0.6s_ease-out_both] lg:text-left">
              <h1 className="text-3xl font-bold leading-tight sm:text-5xl">
                Orçamento pronto em minutos, bonito de mandar no WhatsApp
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-base text-white sm:text-lg lg:mx-0">
                Monte a proposta no celular, envie o PDF e receba o aprovado do cliente pelo link — sem papel, sem
                planilha, sem &ldquo;depois te mando&rdquo;.
              </p>
              <div className="mx-auto mt-8 flex max-w-sm flex-col gap-3 sm:max-w-md sm:flex-row sm:justify-center lg:mx-0 lg:justify-start">
                <Button3D color="light" className="sm:flex-1" onClick={() => navigate('/auth')}>
                  Criar conta grátis
                </Button3D>
                {/* O visitante monta um orçamento inteiro sem cadastro — é o
                    caminho que o onboarding do app já oferece. */}
                <Button3D color="gray" className="sm:flex-1" onClick={() => navigate('/quotes/new')}>
                  Testar sem criar conta
                </Button3D>
              </div>
              <p className="mt-3 text-sm text-white">Grátis não pede cartão · Funciona no navegador, sem instalar nada</p>
              <p className="mt-2 text-sm text-white/80">Feito para quem vive de serviço — MEI, autônomo ou pequena empresa.</p>
            </div>

            {/* Mockup do produto, só CSS: um documento de orçamento em BARRAS
                (nenhum número de dinheiro pode aparecer — guard de preço) e a
                história inteira em dois selos: enviado → aprovado. */}
            <div className="relative mx-auto w-full max-w-[300px] motion-safe:animate-[aparecer_0.6s_ease-out_0.15s_both]">
              {/* O telefone é ILUSTRAÇÃO: sem aria-hidden, o leitor de tela
                  anuncia "Total" e "Enviar por WhatsApp" como texto solto no
                  meio do hero. Os dois selos abaixo ficam legíveis de
                  propósito — eles afirmam fatos do produto. */}
              <div aria-hidden className="mx-auto w-[250px] rounded-[2.25rem] border-4 border-white/25 bg-background p-3 shadow-2xl">
                <div className="rounded-2xl bg-card p-3 text-left shadow-sm">
                  <div className="rounded-lg bg-primary p-2.5">
                    <div className="h-2 w-16 rounded bg-white/80" />
                    <div className="mt-1.5 h-1.5 w-24 rounded bg-white/50" />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="h-6 w-6 shrink-0 rounded-full bg-primary/15" />
                    <div className="min-w-0 flex-1">
                      <div className="h-2 w-20 rounded bg-muted-foreground/40" />
                      <div className="mt-1 h-1.5 w-14 rounded bg-muted-foreground/25" />
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <LinhaDeItem larguraValor="w-10" />
                    <LinhaDeItem larguraValor="w-8" />
                    <LinhaDeItem larguraValor="w-12" />
                  </div>
                  <div className="mt-3 border-t border-dashed border-border" />
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Total</span>
                    <div className="h-3.5 w-16 rounded bg-primary" />
                  </div>
                  {/* Verde de MARCA do WhatsApp (#25D366), não o verde do app —
                      ação de WhatsApp usa a cor da marca (taxonomia #20). */}
                  <div className="mt-3 flex h-8 items-center justify-center gap-1.5 rounded-full bg-[#25d366] text-xs font-semibold text-white shadow-[inset_0_-3px_0_rgba(11,94,60,0.34)]">
                    <Send className="h-3.5 w-3.5" aria-hidden />
                    Enviar por WhatsApp
                  </div>
                </div>
              </div>
              {/* Selos flutuantes: bg-card + text-foreground explícito — a seção
                  seta text-white, e branco sobre o card claro sumiria (#207). */}
              <div className="absolute left-0 top-14 flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs font-semibold text-foreground shadow-lg motion-safe:animate-[flutuar_7s_ease-in-out_infinite]">
                <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
                Aprovado pelo cliente
              </div>
              <div className="absolute bottom-14 right-0 flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs font-semibold text-foreground shadow-lg motion-safe:animate-[flutuar_8s_ease-in-out_1s_infinite]">
                <WifiOff className="h-3.5 w-3.5 text-primary" aria-hidden />
                Funciona offline
              </div>
            </div>
          </div>
        </section>

        {/* ─── Como funciona ─────────────────────────────────────────────── */}
        <section id="como-funciona" className="scroll-mt-24 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Como funciona</h2>
            <ol className="mt-10 grid list-none grid-cols-1 gap-4 sm:grid-cols-3">
              {PASSOS.map(({ titulo, texto }, i) => (
                <li key={titulo} className={CARTAO_VIVO}>
                  <span aria-hidden className="text-4xl font-black text-primary/25">
                    {i + 1}
                  </span>
                  <h3 className="mt-2 font-semibold">{titulo}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ─── Convite ao teste sem cadastro ─────────────────────────────── */}
        <section className="px-4 pb-14 sm:pb-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Quer ver como sai o seu?</h2>
            <div className="mx-auto mt-6 max-w-xs">
              {/* Aqui o teste sem cadastro É a ação principal da seção — verde. */}
              <Button3D className="w-full" onClick={() => navigate('/quotes/new')}>
                Testar sem criar conta
              </Button3D>
            </div>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
              Monte um orçamento de verdade agora, sem cadastro. Se gostar, crie a conta depois — o app oferece
              importar o que você montou.
            </p>
          </div>
        </section>

        {/* ─── O ciclo ───────────────────────────────────────────────────── */}
        <section id="recursos" className="scroll-mt-24 bg-muted/40 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Fechou o serviço? O resto sai dali</h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
              Do orçamento aprovado nascem o contrato, a ordem de serviço, o recibo e a nota — um puxa os dados do
              outro, sem digitar tudo de novo.
            </p>
            <div className="relative mt-10">
              {/* Linha que conecta os selos numerados — só quando as 5 colunas existem. */}
              <div
                aria-hidden
                className="absolute left-8 right-8 top-[38px] hidden h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent xl:block"
              />
              <ol className="grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {CICLO.map(({ icon: Icon, titulo, texto }, i) => (
                  <li key={titulo} className={cn(CARTAO_VIVO, 'relative flex flex-col')}>
                    <div className="mb-3 flex items-center gap-3">
                      {/* Selo com a banda 3D do Button3D (#20) — o número fala a língua do app. */}
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary pb-[3px] text-sm font-bold text-white shadow-[inset_0_-3px_0_rgba(16,78,52,0.35)]">
                        {i + 1}
                      </span>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </span>
                    </div>
                    <h3 className="font-semibold">{titulo}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* ─── Funciona sem internet ─────────────────────────────────────── */}
        <section className="px-4 py-14 sm:py-20">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <div className="relative h-16 w-16 shrink-0">
              <span aria-hidden className="absolute inset-0 rounded-2xl bg-primary/10 motion-safe:animate-ping [animation-duration:3s]" />
              <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <WifiOff className="h-8 w-8" />
              </span>
            </div>
            <div className="flex flex-col items-center sm:items-start">
              <h2 className="text-2xl font-bold sm:text-3xl">Funciona sem internet</h2>
              <p className="mt-2 text-muted-foreground">
                No meio de uma obra, num sítio ou num porão sem sinal, o app continua funcionando: você cadastra o
                cliente, monta o orçamento e tira as fotos. Quando a conexão volta, tudo sobe sozinho.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <div className="h-2 w-full max-w-[220px] overflow-hidden rounded-full bg-primary/15">
                  <div className="h-full rounded-full bg-primary motion-safe:animate-[sincronizar_2.5s_ease-in-out_infinite]" />
                </div>
                <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
                  <Check className="h-3.5 w-3.5" aria-hidden /> sobe sozinho
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Os módulos ────────────────────────────────────────────────── */}
        <section className="bg-muted/40 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">E o resto do negócio junto</h2>
            <ul className="mt-10 grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {MODULOS.map(({ icon: Icon, titulo, texto }) => (
                <li key={titulo} className={CARTAO_VIVO}>
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold">{titulo}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ─── Planos ────────────────────────────────────────────────────── */}
        <section id="planos" className="scroll-mt-24 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Comece de graça</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
              O grátis já resolve o dia a dia: orçamento, OS, recibo, financeiro e agenda. O PRO tira o limite mensal
              e libera contrato, marca e relatórios.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={cn(CARTAO, 'flex flex-col')}>
                <h3 className="text-lg font-bold">Grátis</h3>
                <p className="mt-1 text-sm text-muted-foreground">Para começar hoje, sem cartão — e sem prazo para acabar.</p>
                <ul className="mt-5 space-y-2.5">
                  {NO_PLANO_GRATIS.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {/* A quota existe (quotes_created_in_period) — dizer aqui é mais
                    honesto do que a pessoa descobrir no meio de um serviço. */}
                <p className="mt-3 text-xs text-muted-foreground">No grátis, a criação de orçamentos tem limite mensal.</p>
                <div className="mt-auto pt-6">
                  <Button3D className="w-full" onClick={() => navigate('/auth')}>
                    Criar conta grátis
                  </Button3D>
                </div>
              </div>

              <div className={cn(CARTAO, 'relative flex flex-col overflow-hidden border-primary/40 ring-1 ring-primary/20')}>
                {/* Âmbar = intenção PRO/upsell (taxonomia #20). */}
                <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 to-amber-500" />
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold">PRO</h3>
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    7 dias grátis
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">Tudo do grátis, mais:</p>
                <ul className="mt-5 space-y-2.5">
                  {NO_PLANO_PRO.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  <Button3D color="amber" className="w-full" onClick={() => navigate('/auth')}>
                    Testar o PRO por 7 dias
                  </Button3D>
                </div>
              </div>
            </div>

            {/* Sem preço nesta tela de propósito: ele vive na loja/assinatura, e um
                número escrito aqui viraria mentira na primeira mudança. A ausência
                é DECLARADA porque preço escondido sem explicação lê como pegadinha. */}
            <p className="mx-auto mt-6 max-w-xl text-center text-sm text-muted-foreground">
              O valor do PRO aparece na tela de assinatura, sempre atualizado — por isso ele não está escrito aqui. O
              grátis não expira e não pede cartão.
            </p>
          </div>
        </section>

        {/* ─── Perguntas frequentes ──────────────────────────────────────── */}
        <section id="perguntas" className="scroll-mt-24 bg-muted/40 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-2xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Perguntas de quem está chegando</h2>
            {/* <details> nativo de propósito: sem JS, e as respostas ficam no DOM
                para o crawler (o Accordion do Radix desmonta conteúdo fechado). */}
            <div className="mt-10 space-y-3">
              {PERGUNTAS.map(({ p, r }) => (
                <details key={p} className="group rounded-2xl border border-border/60 bg-card">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl p-4 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                    {p}
                    <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="px-4 pb-4 text-sm text-muted-foreground">{r}</p>
                </details>
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Mais dúvidas?{' '}
              <Link to="/help" className="font-medium text-primary underline-offset-4 hover:underline">
                Veja a central de ajuda
              </Link>
            </p>
          </div>
        </section>

        {/* ─── Chamada final ─────────────────────────────────────────────── */}
        <section className="px-4 py-14 sm:py-20">
          <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-emerald-700 px-6 py-12 text-center text-white shadow-xl sm:px-12">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
            />
            <div className="relative">
              <h2 className="text-2xl font-bold sm:text-3xl">Seu próximo orçamento pode sair hoje</h2>
              <p className="mx-auto mt-3 max-w-md text-white/90">
                Grátis para começar, sem pedir cartão — e dá para experimentar antes mesmo de criar conta.
              </p>
              <div className="mx-auto mt-7 flex max-w-sm flex-col gap-3 sm:max-w-md sm:flex-row sm:justify-center">
                <Button3D color="light" className="sm:flex-1" onClick={() => navigate('/auth')}>
                  Criar conta grátis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button3D>
                <Button3D color="gray" className="sm:flex-1" onClick={() => navigate('/quotes/new')}>
                  Testar sem criar conta
                </Button3D>
              </div>
              <p className="mt-4 text-sm text-white/90">Sem cartão para começar. O PRO tem 7 dias de teste.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 px-4 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 text-sm text-muted-foreground sm:flex-row">
          <span>© Orçamigo</span>
          {/* Links de verdade (<a>): crawler segue e clique do meio funciona. Os
              CTAs seguem Button3D+navigate — é o que o guard de rotas varre. */}
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/terms" className="hover:text-foreground">
              Termos de uso
            </Link>
            <Link to="/privacy" className="hover:text-foreground">
              Privacidade
            </Link>
            <Link to="/help" className="hover:text-foreground">
              Central de ajuda
            </Link>
            <a href="mailto:contato@orcamigo.com.br" className="hover:text-foreground">
              contato@orcamigo.com.br
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/**
 * A rota `/`: vitrine na web, onboarding no app instalado.
 *
 * A escolha é feita aqui, e não dentro de cada página, para que exista UM lugar
 * respondendo "o que é a home" — e para o guard de teste ter o que medir.
 */
export function isLandingPlatform(): boolean {
  return !Capacitor.isNativePlatform();
}
